import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Account, Application, LicenseApi, Profile, ProfileApi, ProfileRequest, Role, RoleApi } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';

@Component({
  selector: 'app-profile-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './profile-modal.html',
  styleUrl: './profile-modal.scss',
})
export class ProfileModal implements OnChanges {
  private readonly licenseApi = inject(LicenseApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly profileApi = inject(ProfileApi);
  private readonly roleApi = inject(RoleApi);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() open = false;
  @Input() preselectedAccountId: number | null = null;
  @Input() profile: Profile | null = null;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly availableAccounts = signal<readonly Account[]>([]);
  readonly availableApplications = signal<readonly Application[]>([]);
  readonly availableRoles = signal<readonly Role[]>([]);
  readonly deleting = signal(false);
  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly roles = signal<readonly Role[]>([]);
  readonly saving = signal(false);
  readonly selectedAccountId = signal<number | null>(null);
  readonly selectedApplicationId = signal<number | null>(null);
  readonly selectedRoleId = signal<number | null>(null);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');
  readonly selectableRoles = computed(() => {
    const applicationId = this.selectedApplicationId();
    if (applicationId === null) {
      return [];
    }

    const selectedIds = new Set(
      this.roles()
        .map((role) => role.id)
        .filter((roleId): roleId is number => roleId !== null),
    );

    return this.availableRoles().filter(
      (role) => role.id !== null && role.application?.id === applicationId && !selectedIds.has(role.id),
    );
  });

  readonly form = this.formBuilder.nonNullable.group({
    description: ['', [Validators.required, Validators.maxLength(512)]],
    name: ['', [Validators.required, Validators.maxLength(256)]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['profile'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && (this.saving() || this.deleting())) {
      return;
    }

    this.closed.emit();
  }

  async requestDelete(): Promise<void> {
    const id = this.selectedId();
    if (id === null) {
      return;
    }

    const keys = [
      'pages.profiles.confirm-delete.cancel',
      'pages.profiles.confirm-delete.confirm',
      'pages.profiles.confirm-delete.message',
      'pages.profiles.confirm-delete.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    const confirmed = await this.confirmDialogService.open({
      cancelText: i18n['pages.profiles.confirm-delete.cancel'],
      confirmText: i18n['pages.profiles.confirm-delete.confirm'],
      message: i18n['pages.profiles.confirm-delete.message'],
      title: i18n['pages.profiles.confirm-delete.title'],
    });

    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      await firstValueFrom(this.profileApi.delete(id));
      this.messageService.success(await this.translate('pages.profiles.messages.delete-success'));
      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.delete-error'));
    } finally {
      this.deleting.set(false);
    }
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      const createdOrUpdatedProfile = this.isCreateMode()
        ? (await firstValueFrom(this.profileApi.create(payload))).body
        : (await firstValueFrom(this.profileApi.update(this.selectedIdOrThrow(), payload))).body;

      this.selectedId.set(createdOrUpdatedProfile.id);
      await this.persistRolesOnSave();

      if (this.isCreateMode()) {
        this.messageService.success(await this.translate('pages.profiles.messages.create-success'));
      } else {
        this.messageService.success(await this.translate('pages.profiles.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  hasError(controlName: keyof ProfileFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  addSelectedRole(): void {
    const selectedRoleId = this.selectedRoleId();
    if (selectedRoleId === null) {
      return;
    }

    const roleToAdd = this.availableRoles().find((role) => role.id === selectedRoleId);
    if (!roleToAdd) {
      return;
    }

    const alreadySelected = this.roles().some((role) => role.id === selectedRoleId);
    if (alreadySelected) {
      return;
    }

    this.roles.set([...this.roles(), roleToAdd]);
    this.selectedRoleId.set(null);
  }

  isInvalid(controlName: keyof ProfileFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  onAccountSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedAccountId.set(value ? Number(value) : null);
  }

  onApplicationSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    const applicationId = value ? Number(value) : null;

    this.selectedApplicationId.set(applicationId);
    this.selectedRoleId.set(null);

    void this.loadRoles();
  }

  onRoleSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedRoleId.set(value ? Number(value) : null);
  }

  removeRole(roleId: number): void {
    const updatedRoles = this.roles().filter((role) => role.id !== roleId);
    this.roles.set(updatedRoles);
  }

  selectAllRoles(): void {
    const applicationId = this.selectedApplicationId();
    if (applicationId === null) {
      return;
    }

    this.roles.set(
      this.availableRoles().filter((role) => role.id !== null && role.application?.id === applicationId),
    );
    this.selectedRoleId.set(null);
  }

  unselectAllRoles(): void {
    this.roles.set([]);
    this.selectedRoleId.set(null);
  }

  private configureFromInputs(): void {
    this.submitted.set(false);

    const profile = this.profile;
    if (!profile) {
      this.availableAccounts.set([]);
      this.availableApplications.set([]);
      this.availableRoles.set([]);
      this.detailMode.set('create');
      this.roles.set([]);
      this.selectedAccountId.set(this.preselectedAccountId);
      this.selectedApplicationId.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedId.set(null);
      this.selectedRoleId.set(null);
      this.selectedUpdatedAt.set(null);

      this.form.reset({
        description: '',
        name: '',
      });

      void this.loadApplications();

      if (this.selectedApplicationId()) {
        void this.loadRoles();
      }
      return;
    }

    this.availableAccounts.set([]);
    this.availableApplications.set([]);
    this.availableRoles.set([]);
    this.detailMode.set('edit');
    this.roles.set(profile.roles ?? []);
    this.selectedAccountId.set(profile.account?.id ?? null);
    this.selectedApplicationId.set(profile.application?.id ?? null);
    this.selectedCreatedAt.set(profile['created-at'] ?? null);
    this.selectedId.set(profile.id);
    this.selectedRoleId.set(null);
    this.selectedUpdatedAt.set(profile['updated-at'] ?? null);

    this.form.reset({
      description: profile.description,
      name: profile.name,
    });

    void this.loadApplications();

    if (this.selectedApplicationId()) {
      void this.loadRoles();
    }
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('Profile id is required for update');
    }
    return id;
  }

  private toApiModel(): ProfileRequest {
    const formValue = this.form.getRawValue();
    const accountId = this.selectedAccountId();
    const applicationId = this.selectedApplicationId();

    if (accountId === null) {
      throw new Error('Account id is required for profile create/update');
    }

    if (applicationId === null) {
      throw new Error('Application id is required for profile create/update');
    }

    return {
      id: this.selectedId(),
      account: { id: accountId },
      application: { id: applicationId },
      description: formValue.description.trim(),
      name: formValue.name.trim(),
    };
  }

  private async loadApplications(): Promise<void> {
    try {
      const licenses = await firstValueFrom(this.licenseApi
        .search(
          {
            payload: {
              page: 0,
              size: 100,
              filters: [
                {
                  field: 'account.id',
                  value: this.preselectedAccountId,
                  comparative: 'eq',
                },
              ],
            }
          }
        )
      );
      const applications: Application[] = licenses && !licenses.error && licenses.body.total > 0
        ? licenses.body.data
          .filter((license) => license.active)
          .map((license) => license.application)
          .filter((application): application is Application => application != null)
        : [];

      this.availableApplications.set(applications);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.applications-load-error'));
    }
  }

  private async loadRoles(): Promise<void> {
    try {
      const response = await firstValueFrom(this.roleApi
        .search(
          {
            payload: {
              page: 0,
              size: 100,
              filters: [
                {
                  field: 'application.id',
                  value: this.selectedApplicationId(),
                  comparative: 'eq',
                },
              ],
            }
          }
        )
      );
      this.availableRoles.set(response.body.data ?? []);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.roles-load-error'));
    }
  }

  private toRoleIdPayload(roles: readonly Role[]): readonly { readonly id: number }[] {
    return roles
      .map((role) => role.id)
      .filter((roleId): roleId is number => roleId !== null)
      .map((roleId) => ({ id: roleId }));
  }

  private async persistRolesOnSave(): Promise<void> {
    const profileId = this.selectedId();
    if (profileId === null) {
      return;
    }

    try {
      await firstValueFrom(this.profileApi.updateRoles(profileId, this.toRoleIdPayload(this.roles())));
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.roles-update-error'));
      throw error;
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface ProfileFormModel {
  description: string;
  name: string;
}
